import { OverlayContainer } from '@angular/cdk/overlay';
import type { ComponentType } from '@angular/cdk/portal';
import { DestroyRef, DOCUMENT, inject, Injectable, type TemplateRef, type ViewContainerRef } from '@angular/core';
import { BrnDialogService, type BrnDialogOptions, type BrnDialogRef } from '@spartan-ng/brain/dialog';

/**
 * Панель подсказки helm-ui — карточка коуча на своём якоре. Она стоит в слое оверлеев поверх окна, но
 * окну не чужая: нажатие по ней — не клик мимо окна.
 */
export const HINT_PANE_CLASS = 'hlm-hint-pane';

/** Pane of a popover that opens on hover (`HlmPopover` `trigger="hover"`): a pointer passing by opened it, not the photographer. */
export const HOVER_POPOVER_PANE_CLASS = 'hlm-hover-popover-pane';

/**
 * An overlay pane the photographer opened — menu, dialog, select, click popover. Passive layers are not
 * windows: tooltips, coach/hint cards that show themselves, hover popovers.
 */
export function isWindowPane(pane: Element): boolean {
  const content = pane.firstElementChild;
  return (
    !!content &&
    !content.matches('[role="tooltip"]') &&
    !pane.classList.contains(HINT_PANE_CLASS) &&
    !pane.classList.contains(HOVER_POPOVER_PANE_CLASS)
  );
}

/** The CDK overlay container, for watchers outside helm-ui. Call in an injection context. */
export function overlayContainerElement(): HTMLElement {
  return inject(OverlayContainer).getContainerElement();
}

// Layers that float over a dialog without belonging to the CDK overlay queue: a press there is not outside.
const OWN_LAYERS = `.${HINT_PANE_CLASS}, hlm-toast-host`;

const stack: BrnDialogRef<unknown>[] = [];

// CDK hands one outside pointer event to EVERY overlay it lands outside of, top-down, so the dialog on
// top is already in its exit animation when the one under it hears about the same click. Topmost is
// therefore «not yet gone», not «not yet closing» — a ref leaves the stack once its close has finished.
function isTopmost(ref: BrnDialogRef<unknown>): boolean {
  return stack[stack.length - 1] === ref;
}

/**
 * Outside clicks and backdrop clicks reach only the top-most dialog — a click on a stacked dialog no
 * longer dismisses the ones underneath it. Escape and every programmatic `close()` stay untouched.
 */
@Injectable()
export class StackAwareBrnDialogService extends BrnDialogService {
  /**
   * Где началось и где кончилось последнее нажатие. Слушаем документ в фазе захвата — раньше, чем
   * `OverlayOutsideClickDispatcher` на `body`, — и к вызову `dismiss('outside')` цель уже известна.
   */
  private pressTargets: (EventTarget | null)[] = [];

  constructor() {
    super();
    const document = inject(DOCUMENT);
    const onPointerDown = (event: Event): void => {
      this.pressTargets = [event.target];
    };

    const onClick = (event: Event): void => {
      this.pressTargets = [this.pressTargets[0] ?? null, event.target];
    };

    const clicks = ['click', 'auxclick', 'contextmenu'];

    document.addEventListener('pointerdown', onPointerDown, true);
    for (const type of clicks) document.addEventListener(type, onClick, true);
    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      for (const type of clicks) document.removeEventListener(type, onClick, true);
    });
  }

  override open<DialogContext, DialogResult = unknown>(
    content: ComponentType<unknown> | TemplateRef<unknown>,
    vcr?: ViewContainerRef,
    context?: DialogContext,
    options?: Partial<BrnDialogOptions>,
  ): BrnDialogRef<DialogResult> {
    const ref = super.open<DialogContext, DialogResult>(content, vcr, context, options);
    stack.push(ref);
    // `closed$` completes on its first emission — nothing to unsubscribe.
    ref.closed$.subscribe(() => {
      const at = stack.indexOf(ref);
      if (at >= 0) stack.splice(at, 1);
    });

    const dismiss = ref.dismiss.bind(ref);
    ref.dismiss = reason => {
      if ((reason === 'outside' || reason === 'backdrop') && !isTopmost(ref)) return false;
      // Подсказка выписана из очереди оверлеев (Escape окна живёт, только пока окно в ней последнее), и
      // CDK считает нажатие по ней кликом мимо окна: крестик карточки закрывал бы окно вместе с ней.
      // The toast host sits above the overlay layer too: a press on a toast must not close the dialog.
      if (reason === 'outside' && this.pressedOnOwnLayer()) return false;
      return dismiss(reason);
    };

    return ref;
  }

  private pressedOnOwnLayer(): boolean {
    return this.pressTargets.some(target => target instanceof Element && !!target.closest(OWN_LAYERS));
  }
}
